import { Image } from 'expo-image';
import { imageUrl } from '@/utils/image-url';
import type { PhotoCaption, ViewerPhoto } from './image-viewer.types';

type PhotoSource = Pick<ViewerPhoto, 'imageUrl' | 'r2Key'>;

/**
 * Composes the single accessibility label a screen reader should announce for a
 * photo in the viewer. The caption bar itself is presentational, so this is the
 * one place the alt text, dish, author, and position are woven into prose.
 *
 * `index` is 0-based; the announced position is 1-based. When the gallery holds
 * a single photo the positional tail ("of N") is dropped entirely. Alt text,
 * when present, leads the label as its own sentence.
 */
export function buildPhotoAccessibilityLabel(
  caption: PhotoCaption | undefined,
  index: number,
  total: number,
  altText?: string | null
): string {
  const base = buildBaseLabel(caption, index, total);
  const alt = altText?.trim();
  return alt ? `${alt}. ${base}` : base;
}

function buildBaseLabel(
  caption: PhotoCaption | undefined,
  index: number,
  total: number
): string {
  const position = index + 1;
  const isSingle = total <= 1;
  const counter = `${position} of ${total}`;

  const dishName = caption?.dishName?.trim();
  const authorName = caption?.author?.name?.trim();

  // No usable metadata -> degrade to a bare "Photo" (optionally positioned).
  if (!dishName && !authorName) {
    return isSingle ? 'Photo' : `Photo ${counter}`;
  }

  // A dish leads the label, attributed to its author when we know one.
  if (dishName) {
    const subject = authorName ? `${dishName} by ${authorName}` : dishName;
    return isSingle ? subject : `${subject}, photo ${counter}`;
  }

  // Author only -> attribute the photo to them.
  const subject = `Photo by ${authorName}`;
  return isSingle ? subject : `${subject}, ${counter}`;
}

export interface ViewerImageSources {
  /** What the viewer displays. */
  full: string;
  /** Shown as the placeholder while `full` loads (usually already cached by the grid). */
  medium: string;
  /** Untransformed upload, used for Save and Share. */
  original: string;
}

/** Resolves the viewer's three URLs for a photo (R2 key preferred over imageUrl). */
export function viewerImageSources(photo: PhotoSource): ViewerImageSources {
  const key = photo.r2Key || photo.imageUrl;
  return {
    full: imageUrl(key, 'full'),
    medium: imageUrl(key, 'medium'),
    original: imageUrl(key, 'original'),
  };
}

/**
 * Warms the memory cache with the full-size URLs the viewer is about to show.
 * Fire-and-forget: a failed prefetch only means the viewer loads normally.
 */
export function prefetchViewerPhotos(photos: readonly PhotoSource[]): void {
  const urls = photos.map((p) => viewerImageSources(p).full).filter(Boolean);
  if (urls.length === 0) return;
  Image.prefetch(urls, 'memory').catch(() => {
    // Best effort; the viewer falls back to a normal load.
  });
}

/** Indices of the photos either side of `index`, within `[0, total)`. */
export function neighborIndices(index: number, total: number): number[] {
  return [index - 1, index + 1].filter((i) => i >= 0 && i < total);
}

const DEFAULT_EXTENSION = 'jpg';
const EXTENSION_PATTERN = /\.([a-z0-9]{2,5})$/i;

/**
 * File name for a saved photo: `flavrly-photo-<id>.<ext>`. The extension comes
 * from the URL path unless `extension` overrides it (the file was re-encoded).
 */
export function downloadFileName(url: string, photoId: string, extension?: string): string {
  if (extension) {
    return `flavrly-photo-${photoId}.${extension}`;
  }
  const path = url.split(/[?#]/)[0] ?? '';
  const lastSegment = path.slice(path.lastIndexOf('/') + 1);
  const match = EXTENSION_PATTERN.exec(lastSegment);
  const urlExtension = match?.[1]?.toLowerCase() ?? DEFAULT_EXTENSION;
  return `flavrly-photo-${photoId}.${urlExtension}`;
}
